import {
  extractValidationMessage,
  isUpstreamTimeoutResponse,
  isValidationError,
} from "~/components/forms/validationErrorResponse";
import { BASKET_REMOVE_FAILED } from "~/lib/constants";

import type {
  BasketSubmissionOutcome,
  BasketSubmissionResult,
  LastQuantityEdit,
} from "./types";

// Last-resort copy when the validation payload carries no message at all
// (empty globalErrors + empty fieldErrors). The real message — including
// SCAPI detail and invariant copy — is surfaced verbatim, matching what
// the inline `<GlobalErrors>` slot rendered before it was removed. The
// basket route's contained `clientAction` (COM-63) returns exactly that empty
// shape for a network-level failure, so this copy IS the shopper-facing
// message for a dropped/blocked basket POST.
const ACTION_FALLBACK_COPY: Record<string, string> = {
  add: "Couldn't add to cart",
  addMany: "Couldn't add to cart",
  update: "Couldn't update cart",
  remove: BASKET_REMOVE_FAILED,
};

/**
 * Should the watcher toast a failed basket submission of this shape?
 *
 *  - Coupon intents: the PromoCodeForm already renders the error inline.
 *  - `remove` marked `sflMove` (COM-63): the guest "Save for later" flow's
 *    basket-remove leg. BasketProvider's remove-settle observer owns that
 *    failure (localStorage rollback + SFL_UPDATE_FAILED toast); a second
 *    "Couldn't remove" toast here would be misleading.
 *  - `add` with an `upstreamTimeout` payload AND `uncertainAddOwner=tile`
 *    (COM-804): the write's outcome is UNKNOWN and AddToCart (the only
 *    submitter running `useBasketAddReconcile`) owns that UX with its own
 *    keyed "Confirming" toast. A watcher toast would sit next to it with a
 *    contradictory message. Adds without the sentinel (PDP buy form, sticky
 *    ATC bar, buy-again) have no reconcile protocol, so the watcher toast is
 *    their only feedback. `addMany` and other intents keep the watcher toast.
 *    The sentinel alone is not enough: the owner must still be MOUNTED and
 *    armed (`options.uncertainAddOwned`, computed by the caller from the live
 *    claim registry in `uncertain-add-claims.ts`; this file stays pure) —
 *    otherwise the add's outcome would reach the shopper through no channel.
 *    The sentinel is readable here because the submission event carries the
 *    re-read form body (COM-830).
 */
export function shouldSurfaceBasketFailureToast(
  formAction: string | undefined,
  formData: Pick<FormData, "get"> | undefined,
  payload?: unknown,
  options: { uncertainAddOwned?: boolean } = {},
): boolean {
  if (
    formAction === "add" &&
    formData?.get("uncertainAddOwner") === "tile" &&
    options.uncertainAddOwned === true &&
    isUpstreamTimeoutResponse(payload)
  ) {
    return false;
  }
  if (formAction === "addCoupon" || formAction === "removeCoupon") return false;
  if (formAction === "remove" && formData?.get("sflMove") === "true") {
    return false;
  }
  return true;
}

/**
 * SFCC-11821 — should an open Quick View close for this settled submission?
 * Only a SUCCESSFUL add stamped `quickView=true` (ProductBuyForm sets it inside
 * the dialog) that STARTED after the dialog mounted (`seq > mountedAtSeq`): an
 * add from a previous, already-closed Quick View that lands late must not close
 * the next one (dev-regression triage 2026-09-09).
 */
export function isQuickViewAddSuccess(
  result: BasketSubmissionResult,
  mountedAtSeq: number,
): boolean {
  if (result.seq <= mountedAtSeq) return false;
  if (result.formData.get("quickView") !== "true") return false;
  const data = result.data as { successfullyAdded?: unknown } | null;
  return !!data && typeof data === "object" && data.successfullyAdded === true;
}

/**
 * What the added-to-basket watcher does with one settled basket submission:
 * open the "Added to your cart" modal, toast the failure, or nothing.
 *
 * `suppressedByRoute` is the COM-242 D17 route handle; the per-submission
 * `suppressAddedToBasketModal` form flag suppresses the same way. Neither
 * suppresses a failure toast.
 */
export function classifyBasketSubmission(
  result: Pick<BasketSubmissionResult, "formData" | "data">,
  options: { suppressedByRoute: boolean; uncertainAddOwned?: boolean },
): BasketSubmissionOutcome {
  const { formData, data } = result;
  if (!data) return { kind: "ignore" };

  if (isValidationError(data)) {
    const intent = (formData.get("action") as string | null) ?? undefined;
    if (
      !shouldSurfaceBasketFailureToast(intent, formData, data, {
        uncertainAddOwned: options.uncertainAddOwned,
      })
    ) {
      return { kind: "ignore" };
    }
    // Show the actual server message verbatim; the per-action fallback only
    // applies when the payload carries no message at all.
    const message =
      extractValidationMessage(data) ??
      ACTION_FALLBACK_COPY[intent ?? ""] ??
      "Something went wrong";
    return { kind: "toast-error", message, intent, payload: data };
  }

  if (
    typeof data !== "object" ||
    !("successfullyAdded" in data) ||
    !data.successfullyAdded
  ) {
    return { kind: "ignore" };
  }
  if (
    options.suppressedByRoute ||
    formData.get("suppressAddedToBasketModal") === "true"
  ) {
    return { kind: "ignore" };
  }
  return { kind: "open-modal", data };
}

/**
 * COM-831 — the quantity the NEXT stepper edit truly starts from. The stepper
 * re-enables when the action settles, but the displayed quantity only moves
 * once the trailing basket reload lands; if the display is unchanged since the
 * last edit, that edit's target is the basket's real quantity (otherwise the
 * stale display value would be sent as `oldQuantity`, emitting a phantom
 * delta). The delta's sign decides add_to_cart vs remove_from_cart
 * (SFCC-12175 W5). A clamped edit (server landed a value other than `to`) is
 * corrected by the reload, which moves `current` and so drops the record.
 */
export function resolveEditBaseline(
  last: LastQuantityEdit | null,
  current: number,
): number {
  return last && last.from === current ? last.to : current;
}

/** The record to keep after submitting an edit to `to` from display `current`. */
export function nextLastEdit(current: number, to: number): LastQuantityEdit {
  return { from: current, to };
}

/** True when a settled `update` action did NOT succeed: the line never moved,
 *  so the in-window baseline record must be dropped. */
export function isFailedEditSettle(data: unknown): boolean {
  return !(data && typeof data === "object" && "successfullyEdited" in data);
}
